import { formatGameClock } from '@parlay-picker/shared/formatting';
import type { GameStatus, QuarterScore } from '@parlay-picker/shared/hooks';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { type ReactElement, useMemo } from 'react';
import { AnimatedNumber } from '../AnimatedNumber';
import { TeamLogo } from '../TeamLogo';

export function GameScoreboard({
  homeTeam,
  awayTeam,
  quarterScores,
  quarter,
  timeRemaining,
  gameState,
  leagueKey,
}: {
  homeTeam: { name: string; abbr: string; score: number };
  awayTeam: { name: string; abbr: string; score: number };
  quarterScores: QuarterScore[];
  quarter: number | null;
  timeRemaining: string | null;
  gameState: GameStatus;
  leagueKey: LeagueKey;
}): ReactElement {
  const statusText = formatGameClock({ gameStatus: gameState, quarter, timeRemaining });

  // Quarter columns
  const quarterCols = useMemo(() => {
    const cols = ['Q1', 'Q2', 'Q3', 'Q4'];
    if (quarterScores.length > 4) {
      for (let i = 4; i < quarterScores.length; i++) {
        cols.push(`OT${i - 3}`);
      }
    }
    return cols;
  }, [quarterScores]);

  return (
    <div className="border-b border-theme-border/50 pb-3">
      {/* Team scores */}
      <div className="flex items-center justify-between px-2">
        {/* Away */}
        <div className="flex items-center gap-3">
          <TeamLogo name={awayTeam.name} leagueKey={leagueKey} size="lg" />
          <div>
            <div className="font-semibold text-sm text-white">{awayTeam.abbr}</div>
            {awayTeam.name !== awayTeam.abbr && (
              <div className="text-xs text-theme-text-tertiary">
                {awayTeam.name.split(' ').pop()}
              </div>
            )}
          </div>
        </div>

        {/* Center: scores + status */}
        <div className="text-center">
          <div className="text-2xl font-bold font-mono tracking-tight">
            {/* Only show a scoreline once the game is known to have started. A status
                we could not interpret would otherwise render "0 - 0" as if it had. */}
            {gameState === 'live' || gameState === 'final' ? (
              <>
                <AnimatedNumber value={awayTeam.score} />
                <span className="text-theme-text-tertiary mx-3">-</span>
                <AnimatedNumber value={homeTeam.score} />
              </>
            ) : (
              <span className="text-theme-text-tertiary text-lg">vs</span>
            )}
          </div>
          {(gameState === 'live' || gameState === 'final') && (
            <div className="flex items-center justify-center gap-2 mt-1">
              {statusText && (
                <span className="text-xs text-theme-text-secondary">{statusText}</span>
              )}
              {gameState === 'live' && (
                <span className="text-[10px] px-1.5 py-0.5 rounded text-white font-bold bg-theme-danger">
                  <span className="inline-block w-1.5 h-1.5 bg-white rounded-full mr-1 animate-pulse" />
                  LIVE
                </span>
              )}
              {gameState === 'final' && (
                <span className="text-[10px] bg-theme-surface-hover px-1.5 py-0.5 rounded text-white font-medium">
                  FINAL
                </span>
              )}
            </div>
          )}
        </div>

        {/* Home */}
        <div className="flex items-center gap-3 flex-row-reverse">
          <TeamLogo name={homeTeam.name} leagueKey={leagueKey} size="lg" />
          <div className="text-right">
            <div className="font-semibold text-sm text-white">{homeTeam.abbr}</div>
            {homeTeam.name !== homeTeam.abbr && (
              <div className="text-xs text-theme-text-tertiary">
                {homeTeam.name.split(' ').pop()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quarter-by-quarter scores */}
      {quarterScores.length > 0 && (
        <div className="mt-3 pt-3 border-t border-theme-border/30 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-theme-text-tertiary">
                <th className="text-left font-normal w-14" />
                {quarterCols.map((col) => (
                  <th key={col} className="text-center font-normal px-2 py-1">
                    {col}
                  </th>
                ))}
                <th className="text-center font-medium px-2 py-1 text-theme-text-secondary">T</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="text-left font-medium text-theme-text-secondary">{awayTeam.abbr}</td>
                {quarterCols.map((_, idx) => (
                  <td
                    key={idx}
                    className="text-center px-2 py-1 text-theme-text-secondary font-mono"
                  >
                    {quarterScores[idx]?.awayScore ?? '--'}
                  </td>
                ))}
                <td className="text-center font-bold text-white px-2 py-1 font-mono">
                  {awayTeam.score}
                </td>
              </tr>
              <tr>
                <td className="text-left font-medium text-theme-text-secondary">{homeTeam.abbr}</td>
                {quarterCols.map((_, idx) => (
                  <td
                    key={idx}
                    className="text-center px-2 py-1 text-theme-text-secondary font-mono"
                  >
                    {quarterScores[idx]?.homeScore ?? '--'}
                  </td>
                ))}
                <td className="text-center font-bold text-white px-2 py-1 font-mono">
                  {homeTeam.score}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
